import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { ApiStatus, CandidateDetailsAction } from '@constants';
import { candidateDetailsStoreKey } from './candidateDetails.const';
import { setImpersonation } from '@redux/accounts';

import { ICandidateDetailsSliceState } from './candidateDetails.types';
import {
  cancelCandidateInterview,
  fetchCandidateDetails,
  fetchCandidateSummary,
  fetchCandidateVideoInterviewUrl,
  provideCandidateFeedback,
  provideClientInterviewFeedback,
  rescheduleCandidateInterview,
  sendCandidateQuestion,
  toggleEmailNotifications,
  fetchNotificationStatus,
} from './candidateDetails.thunk';

const initialState: ICandidateDetailsSliceState = {
  apiStatus: ApiStatus.IDLE,
  data: null,
  provideClientFeedbackAPIStatus: ApiStatus.IDLE,
  cancelCandidateInterviewAPIStatus: ApiStatus.IDLE,
  rescheduleCandidateInterviewAPIStatus: ApiStatus.IDLE,
  provideClientInterviewFeedbackAPIStatus: ApiStatus.IDLE,
  sendCandidateQuestionAPIStatus: ApiStatus.IDLE,
  writeError: null,
  candidateDetailsAction: null,
  candidateSummary: null,
  candidateSummaryApiStatus: ApiStatus.IDLE,
  selectedVideoInterviewUrl: null,
  selectedVideoInterviewUrlApiStatus: ApiStatus.IDLE,
  notificationsSettings: null,
  switchNotificationsStatus: ApiStatus.IDLE,
  fetchNotificationsStatus: ApiStatus.IDLE,
};

/**
 * A refused write, said out loud.
 *
 * Every write path used to fail in silence: the dialog stayed open, nothing appeared,
 * and the reader's text was lost. Three 403s were produced in one review session and
 * none of them reached the screen. The server refuses for two quite different reasons
 * and the reader needs to be told which — "you may not do this" and "somebody got there
 * first" ask for different next moves. See
 * docs/client-dashboard/handoff-hiring-flow-gaps.md §B5.
 */
export const writeErrorFrom = (payload: any, stale: string): string => {
  if (payload?.statusCode === 403)
    return payload?.message === 'insufficient permissions'
      ? 'Your access to this account is read-only, so nothing was sent.'
      : stale;

  if (payload?.statusCode === 404)
    return 'This candidate is no longer on this account. Reload the page.';

  if (typeof payload?.statusCode === 'number' && payload.statusCode < 500)
    return (
      [payload?.message].flat().filter(Boolean).join(', ') ||
      'That request was rejected.'
    );

  return 'Something went wrong on our side and nothing was saved. Try again in a moment.';
};

/** The three interview endpoints refuse a round somebody has already written up. */
const ROUND_IS_CLOSED =
  'This interview has already been written up. Reload to see the latest.';

const candidateDetailsSlice = createSlice({
  name: candidateDetailsStoreKey,
  initialState,
  reducers: {
    clearCandidateDetails: () => initialState,
    clearCandidateSummary: () => initialState,
    setCandidateDetailsAction: (
      state,
      action: PayloadAction<CandidateDetailsAction | null>,
    ) => {
      state.candidateDetailsAction = action.payload;
      // Opening or closing a dialog starts the question again; a stale rejection
      // sitting under the next form would be answering the wrong one.
      state.writeError = null;
    },
    setLowHappinessNotificationSetting: (state, action) => {
      if (state.notificationsSettings) {
        state.notificationsSettings.isLowHappiness = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCandidateDetails.pending, (state) => {
        state.apiStatus = ApiStatus.LOADING;
      })
      .addCase(fetchCandidateDetails.fulfilled, (state, action) => {
        state.apiStatus = ApiStatus.COMPLETE;
        state.data = action.payload;
      })
      .addCase(
        fetchCandidateDetails.rejected,
        (state, action: PayloadAction<any>) => {
          state.apiStatus =
            action?.payload?.statusCode === 403
              ? ApiStatus.FORBIDDEN
              : ApiStatus.FAILED;
        },
      )
      .addCase(setImpersonation.pending, () => ({
        ...initialState,
        apiStatus: ApiStatus.LOADING,
      }))
      .addCase(provideCandidateFeedback.pending, (state) => {
        state.provideClientFeedbackAPIStatus = ApiStatus.LOADING;
        state.writeError = null;
      })
      .addCase(provideCandidateFeedback.fulfilled, (state, action) => {
        state.provideClientFeedbackAPIStatus = ApiStatus.COMPLETE;
        state.data = action.payload;
      })
      .addCase(
        provideCandidateFeedback.rejected,
        (state, action: PayloadAction<any>) => {
          state.provideClientFeedbackAPIStatus = ApiStatus.FAILED;
          state.writeError = writeErrorFrom(
            action?.payload,
            'This candidate has already been decided on. Reload to see the latest.',
          );
        },
      )
      .addCase(cancelCandidateInterview.pending, (state) => {
        state.cancelCandidateInterviewAPIStatus = ApiStatus.LOADING;
        state.writeError = null;
      })
      .addCase(cancelCandidateInterview.fulfilled, (state, action) => {
        state.cancelCandidateInterviewAPIStatus = ApiStatus.COMPLETE;
        state.data = action.payload;
      })
      .addCase(
        cancelCandidateInterview.rejected,
        (state, action: PayloadAction<any>) => {
          state.cancelCandidateInterviewAPIStatus = ApiStatus.FAILED;
          state.writeError = writeErrorFrom(action?.payload, ROUND_IS_CLOSED);
        },
      )
      .addCase(rescheduleCandidateInterview.pending, (state) => {
        state.rescheduleCandidateInterviewAPIStatus = ApiStatus.LOADING;
        state.writeError = null;
      })
      .addCase(rescheduleCandidateInterview.fulfilled, (state, action) => {
        state.rescheduleCandidateInterviewAPIStatus = ApiStatus.COMPLETE;
        state.data = action.payload;
      })
      .addCase(
        rescheduleCandidateInterview.rejected,
        (state, action: PayloadAction<any>) => {
          state.rescheduleCandidateInterviewAPIStatus = ApiStatus.FAILED;
          state.writeError = writeErrorFrom(action?.payload, ROUND_IS_CLOSED);
        },
      )
      .addCase(provideClientInterviewFeedback.pending, (state) => {
        state.provideClientInterviewFeedbackAPIStatus = ApiStatus.LOADING;
        state.writeError = null;
      })
      .addCase(provideClientInterviewFeedback.fulfilled, (state, action) => {
        state.provideClientInterviewFeedbackAPIStatus = ApiStatus.COMPLETE;
        state.data = action.payload;
      })
      .addCase(
        provideClientInterviewFeedback.rejected,
        (state, action: PayloadAction<any>) => {
          state.provideClientInterviewFeedbackAPIStatus = ApiStatus.FAILED;
          state.writeError = writeErrorFrom(action?.payload, ROUND_IS_CLOSED);
        },
      )
      .addCase(fetchCandidateSummary.pending, (state) => {
        state.candidateSummaryApiStatus = ApiStatus.LOADING;
      })
      .addCase(fetchCandidateSummary.fulfilled, (state, action) => {
        state.candidateSummaryApiStatus = ApiStatus.COMPLETE;
        state.candidateSummary = action.payload;
      })
      .addCase(fetchCandidateSummary.rejected, (state) => {
        state.candidateSummaryApiStatus = ApiStatus.FAILED;
      })
      .addCase(fetchCandidateVideoInterviewUrl.pending, (state) => {
        state.selectedVideoInterviewUrlApiStatus = ApiStatus.LOADING;
      })
      .addCase(fetchCandidateVideoInterviewUrl.fulfilled, (state, action) => {
        state.selectedVideoInterviewUrlApiStatus = ApiStatus.COMPLETE;
        state.selectedVideoInterviewUrl = action.payload;
      })
      .addCase(fetchCandidateVideoInterviewUrl.rejected, (state) => {
        state.selectedVideoInterviewUrlApiStatus = ApiStatus.FAILED;
      })
      .addCase(sendCandidateQuestion.pending, (state) => {
        state.sendCandidateQuestionAPIStatus = ApiStatus.LOADING;
        state.writeError = null;
      })
      .addCase(sendCandidateQuestion.fulfilled, (state, action) => {
        state.sendCandidateQuestionAPIStatus = ApiStatus.COMPLETE;
        state.data = action.payload;
      })
      .addCase(
        sendCandidateQuestion.rejected,
        (state, action: PayloadAction<any>) => {
          state.sendCandidateQuestionAPIStatus = ApiStatus.FAILED;
          state.writeError = writeErrorFrom(
            action?.payload,
            'This question was not accepted. Reload to see the latest.',
          );
        },
      )
      .addCase(toggleEmailNotifications.pending, (state) => {
        state.switchNotificationsStatus = ApiStatus.LOADING;
      })
      .addCase(toggleEmailNotifications.fulfilled, (state, action) => {
        state.switchNotificationsStatus = ApiStatus.COMPLETE;
        state.notificationsSettings = action.payload;
      })
      .addCase(toggleEmailNotifications.rejected, (state) => {
        state.switchNotificationsStatus = ApiStatus.FAILED;
      })
      .addCase(fetchNotificationStatus.pending, (state) => {
        state.fetchNotificationsStatus = ApiStatus.LOADING;
      })
      .addCase(fetchNotificationStatus.fulfilled, (state, action) => {
        state.fetchNotificationsStatus = ApiStatus.COMPLETE;
        state.notificationsSettings = action.payload;
      })
      .addCase(fetchNotificationStatus.rejected, (state) => {
        state.fetchNotificationsStatus = ApiStatus.FAILED;
      });
  },
});

export const {
  clearCandidateDetails,
  clearCandidateSummary,
  setCandidateDetailsAction,
  setLowHappinessNotificationSetting,
} = candidateDetailsSlice.actions;
export const candidateDetailsSliceReducer = candidateDetailsSlice.reducer;
